/**
 * Chat modul — nastavení
 * Profily LLM (lokální + cloud), tool picker, systémový prompt.
 * Drag & drop pořadí profilů via @dnd-kit/sortable.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// dnd-kit imports moved to LlmProfileEditor
import {
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  MessageCircle,
  Plus,
  Server,
  Wrench,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
// dnd-kit is encapsulated inside LlmProfileEditor
import { Badge, Button, PageHeader, Tab, TabList, TabPanel, Tabs } from '../../components/ui';
import { ToolGroupPicker, type ToolInfo } from '../../components/ToolGroupPicker';
import { type LlmProfile, LlmProfileEditor } from '../../components/ui/LlmProfileEditor';
import { api } from '../../lib/api';

// ── Typy ──────────────────────────────────────────────────────────────────────

// LlmProfile type imported from LlmProfileEditor

interface ChatConfig {
  ok: boolean;
  enabled: string;
  profiles: LlmProfile[];
  resolved_tools: string[];
  system_prompt: string;
}

// ── Fetch helpers (MCP) ───────────────────────────────────────────────────────

const fetchConfig = (): Promise<ChatConfig> =>
  api.mcp<ChatConfig>('chat.config_get', {});
const fetchUsage = (): Promise<{ usage: Record<string, { today: number; limit: number }> }> =>
  api.mcp('chat.usage', {});
const fetchAllToolGroups = (): Promise<{ by_module: Record<string, ToolInfo[]>; total: number }> =>
  api.mcp('chat.all_tools', {});
const fetchDefPrompt = (): Promise<{ prompt: string }> =>
  api.mcp('chat.default_prompt', {});

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, icon: Icon, color, badge, children }: {
  title: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  color?: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div className='bg-gray-900 border border-gray-800 rounded-2xl p-5'>
      <div className='flex items-center gap-2 mb-1'>
        <Icon size={14} className={color} />
        <span className='text-sm font-semibold text-white'>{title}</span>
        {badge && <Badge variant='default'>{badge}</Badge>}
      </div>
      {children}
    </div>
  );
}

// ── ProfileEditor ─────────────────────────────────────────────────────────────

// ── SortableProfileItem ───────────────────────────────────────────────────────

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ChatPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: cfg, isLoading, error } = useQuery({
    queryKey: ['chat-config-full'],
    queryFn: fetchConfig,
    staleTime: 30_000,
  });
  const { data: usageData } = useQuery({
    queryKey: ['chat-usage'],
    queryFn: fetchUsage,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  const { data: toolGroupsData } = useQuery({
    queryKey: ['chat-all-tools'],
    queryFn: fetchAllToolGroups,
    staleTime: 300_000,
  });

  const initialized = useRef(false);
  const [profiles, setProfiles] = useState<LlmProfile[]>([]);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());
  const [systemPrompt, setSystemPrompt] = useState('');
  const [promptMode, setPromptMode] = useState<'default' | 'custom'>('default');
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  const defaultPromptQuery = useQuery({
    queryKey: ['chat-default-prompt'],
    queryFn: fetchDefPrompt,
    enabled: showPromptPreview || promptMode === 'default',
    staleTime: 300_000,
  });

  useEffect(() => {
    if (!cfg || initialized.current) return;
    initialized.current = true;
    setProfiles(cfg.profiles ?? []);
    setSelectedTools(new Set(cfg.resolved_tools ?? []));
    if (cfg.system_prompt?.trim()) {
      setSystemPrompt(cfg.system_prompt);
      setPromptMode('custom');
    }
  }, [cfg]);

  const toggleMut = useMutation({
    mutationFn: () =>
      api.mcp('chat.config_set', {
        enabled: cfg?.enabled === 'true' ? 'false' : 'true',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-config-full'] });
      toast.success(cfg?.enabled === 'true' ? t('chat.toastDisabled') : t('chat.toastEnabled'));
    },
    onError: () => toast.error(t('chat.toastToggleFailed')),
  });

  const saveProfilesMut = useMutation({
    mutationFn: (profs: LlmProfile[]) =>
      api.mcp('chat.profiles_set', { profiles: profs }),
    onSuccess: () => {
      toast.success(t('chat.toastProfilesSaved'));
      initialized.current = false;
      qc.invalidateQueries({ queryKey: ['chat-config-full'] });
      qc.invalidateQueries({ queryKey: ['chat-usage'] });
    },
    onError: (e) => toast.error(t('chat.toastSaveFailed', { message: (e as Error).message })),
  });

  const saveSettingsMut = useMutation({
    mutationFn: () =>
      api.mcp('chat.config_set', {
        allowed_tools: [...selectedTools].join(','),
        system_prompt: promptMode === 'custom' ? systemPrompt : '',
      }),
    onSuccess: () => {
      toast.success(t('chat.toastSettingsSaved'));
      initialized.current = false;
      qc.invalidateQueries({ queryKey: ['chat-config-full'] });
    },
    onError: (e) => toast.error(t('chat.toastSaveFailed', { message: (e as Error).message })),
  });

  function reindex(arr: LlmProfile[]): LlmProfile[] {
    return arr.map((p, i) => ({ ...p, priority: i }));
  }

  function saveProfiles(arr: LlmProfile[]) {
    const indexed = reindex(arr);
    setProfiles(indexed);
    saveProfilesMut.mutate(indexed);
  }

  // Profily z hlavního llm-profiles registru (source==='shared') se tady jen
  // zobrazují — úprava/enable/disable/smazání patří do LLM Profiles modulu.
  function isSharedReadOnly(idx: number): boolean {
    if (profiles[idx]?.source === 'shared') {
      toast.error(t('chat.sharedProfileReadOnly', 'Profil z hlavního seznamu — uprav ho v LLM Profiles modulu.'));
      return true;
    }
    return false;
  }

  function updateProfile(idx: number, p: LlmProfile) {
    if (isSharedReadOnly(idx)) return;
    const next = [...profiles];
    next[idx] = p;
    saveProfiles(next);
  }

  function toggleProfile(idx: number, enabled: boolean) {
    // Shared profil: obsah je read-only, ale zapnuti V CHATU je per-chat
    // stav (backend ho uklada do shared_profile_toggles) — toggle povolen.
    const next = [...profiles];
    next[idx] = { ...next[idx]!, enabled };
    saveProfiles(next);
  }

  function deleteProfile(idx: number) {
    if (isSharedReadOnly(idx)) return;
    saveProfiles(profiles.filter((_, i) => i !== idx));
  }

  function cloneProfile(src: LlmProfile) {
    const newP: LlmProfile = {
      ...src,
      id: `profile-${Date.now()}`,
      label: t('chat.cloneLabel', { label: src.label }),
      enabled: false, // klon defaultně off — operator zapne až po úpravě
      priority: profiles.length,
      source: 'own', // klon je vždy vlastní kopie, i když src bylo 'shared'
      api_key: src.source === 'shared' ? '' : src.api_key, // shared má jen '***' — klon potřebuje reálný klíč
    };
    setProfiles((prev) => reindex([...prev, newP]));
    saveProfilesMut.mutate(reindex([...profiles, newP]));
  }

  function addProfile() {
    const newP: LlmProfile = {
      id: `profile-${Date.now()}`,
      label: t('chat.newProfileLabel'),
      url: '',
      model: '',
      api_key: '',
      backend: 'openai',
      enabled: false,
      priority: profiles.length,
      daily_token_limit: 0,
    };
    setProfiles((prev) => reindex([...prev, newP]));
  }

  if (isLoading) return <div className='text-gray-400 text-sm p-6'>{t('chat.loading')}</div>;
  if (error) {
    return (
      <div className='text-red-400 text-sm p-6'>{t('chat.error', { message: String(error) })}</div>
    );
  }

  const enabled = cfg?.enabled === 'true';
  const enabledCount = profiles.filter((p) => p.enabled).length;

  return (
    <div className='space-y-4'>
      <PageHeader
        title={t('chat.title')}
        description={t('chat.subtitle')}
        icon={MessageCircle}
      />

      <Tabs defaultTab='profiles'>
        <TabList>
          <Tab id='profiles'>
            <Server size={12} />{t('chat.tabProfiles')}
          </Tab>
          <Tab id='tools'>
            <Wrench size={12} />{t('chat.tabTools')}
          </Tab>
          <Tab id='prompt'>
            <FileText size={12} />{t('chat.tabPrompt')}
          </Tab>
        </TabList>

        {/* ── PROFILY ── */}
        <TabPanel id='profiles'>
          <div className='space-y-4 max-w-2xl'>
            {/* Enable toggle */}
            <div className='bg-gray-900 border border-gray-800 rounded-2xl p-5 flex items-center justify-between'>
              <div>
                <div className='text-sm font-semibold text-white mb-0.5'>
                  {t('chat.assistantState')}
                </div>
                <div className={`text-xs ${enabled ? 'text-green-400' : 'text-gray-500'}`}>
                  {enabled
                    ? t('chat.activeProfiles', { count: enabledCount })
                    : t('chat.off')}
                </div>
              </div>
              <button
                onClick={() => toggleMut.mutate()}
                disabled={toggleMut.isPending}
                className='flex items-center gap-2.5 disabled:opacity-50'
              >
                <span
                  className={`text-sm font-medium ${enabled ? 'text-green-400' : 'text-gray-500'}`}
                >
                  {enabled ? t('chat.on') : t('chat.off')}
                </span>
                <div
                  style={{
                    backgroundColor: enabled ? '#16a34a' : '#374151',
                    transition: 'background-color 0.2s',
                  }}
                  className='relative w-12 h-7 rounded-full flex-shrink-0'
                >
                  <div
                    style={{
                      transform: enabled ? 'translateX(22px)' : 'translateX(3px)',
                      transition: 'transform 0.2s',
                      top: '3px',
                    }}
                    className='absolute w-[22px] h-[22px] rounded-full bg-white shadow-md'
                  />
                </div>
              </button>
            </div>

            {/* Profily — drag & drop */}
            <Section
              title={t('chat.llmProfiles')}
              icon={Zap}
              color='text-blue-400'
              badge={t('chat.activeBadge', { count: enabledCount })}
            >
              <div className='mt-3 space-y-1'>
                <p className='text-[11px] text-gray-500 mb-3'>
                  {t('chat.profilesHelpStart')}{' '}
                  <GripVertical size={10} className='inline' />{' '}
                  {t('chat.profilesHelpEnd')}
                </p>
                <LlmProfileEditor
                  profiles={profiles}
                  onSave={(p) => {
                    const idx = profiles.findIndex((x) => x.id === p.id);
                    if (idx >= 0) updateProfile(idx, p);
                  }}
                  onClone={cloneProfile}
                  onDelete={(id) => {
                    const idx = profiles.findIndex((x) => x.id === id);
                    if (idx >= 0) deleteProfile(idx);
                  }}
                  onToggle={(id, en) => {
                    const idx = profiles.findIndex((x) => x.id === id);
                    if (idx >= 0) toggleProfile(idx, en);
                  }}
                  onReorder={(orderedIds) => {
                    const reordered = orderedIds
                      .map((id) => profiles.find((p) => p.id === id))
                      .filter((p): p is LlmProfile => !!p);
                    saveProfiles(reordered);
                  }}
                  usage={usageData?.usage}
                />
                <button
                  onClick={addProfile}
                  className='w-full flex items-center justify-center gap-2 py-2 mt-2 border border-dashed border-gray-700 rounded-xl text-sm text-gray-500 hover:text-gray-300 hover:border-gray-500 transition-colors'
                >
                  <Plus size={14} />{t('chat.addProfile')}
                </button>

                {/* Celkový CAP */}
                {(() => {
                  const active = profiles.filter((p) => p.enabled);
                  const totalLimit = active.reduce(
                    (s, p) => p.daily_token_limit > 0 ? s + p.daily_token_limit : s,
                    0,
                  );
                  const totalUsed = active.reduce(
                    (s, p) => s + (usageData?.usage?.[p.id]?.today ?? 0),
                    0,
                  );
                  const hasLimit = active.some((p) => p.daily_token_limit > 0);
                  if (active.length === 0) return null;
                  const pct = hasLimit && totalLimit > 0
                    ? Math.min(100, Math.round(totalUsed / totalLimit * 100))
                    : null;
                  return (
                    <div className='border border-gray-800 rounded-xl p-3 bg-gray-900/30 space-y-2 mt-1'>
                      <div className='flex items-center justify-between text-xs'>
                        <span className='text-gray-400 font-medium'>{t('chat.totalUsageToday')}</span>
                        <span className='text-gray-300 font-mono'>
                          {totalUsed.toLocaleString()} /{' '}
                          {hasLimit ? totalLimit.toLocaleString() : '∞'} {t('chat.tokens')}
                        </span>
                      </div>
                      {pct !== null && (
                        <div>
                          <div className='h-1.5 bg-gray-800 rounded-full overflow-hidden'>
                            <div
                              className={`h-full rounded-full transition-all ${
                                pct >= 90
                                  ? 'bg-red-500'
                                  : pct >= 70
                                  ? 'bg-yellow-500'
                                  : 'bg-blue-500'
                              }`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className='flex justify-between text-[10px] text-gray-600 mt-0.5'>
                            <span>{t('chat.activeProfilesCount', { count: active.length })}</span>
                            <span>{t('chat.pctOfTotalLimit', { pct })}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </Section>
          </div>
        </TabPanel>

        {/* ── NÁSTROJE ── */}
        <TabPanel id='tools'>
          <div className='space-y-4 max-w-2xl'>
            <Section
              title={t('chat.allowedTools')}
              icon={Wrench}
              color='text-yellow-400'
              badge={selectedTools.size > 0 ? t('chat.selectedBadge', { count: selectedTools.size }) : undefined}
            >
              <div className='mt-3'>
                <div className='flex items-center gap-2 mb-3'>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      const all = new Set<string>();
                      Object.values(toolGroupsData?.by_module ?? {}).forEach((mod: unknown) => {
                        (mod as { tools: ToolInfo[] }).tools?.forEach((tool) => all.add(tool.name));
                      });
                      setSelectedTools(all);
                    }}
                  >
                    {t('chat.selectAll', {
                      count: Object.values(toolGroupsData?.by_module ?? {})
                        .reduce(
                          (s: number, m: unknown) =>
                            s + ((m as { tools: ToolInfo[] }).tools?.length ?? 0),
                          0,
                        ),
                    })}
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => setSelectedTools(new Set())}
                  >
                    {t('chat.clearSelection')}
                  </Button>
                </div>
                <ToolGroupPicker
                  groups={toolGroupsData?.by_module ?? {}}
                  selected={selectedTools}
                  onChange={setSelectedTools}
                  maxHeight={80}
                />
              </div>
            </Section>
            <div className='flex justify-end'>
              <Button
                variant='primary'
                loading={saveSettingsMut.isPending}
                onClick={() => saveSettingsMut.mutate()}
              >
                {t('chat.saveTools')}
              </Button>
            </div>
          </div>
        </TabPanel>

        {/* ── SYSTEM PROMPT ── */}
        <TabPanel id='prompt'>
          <div className='space-y-4 max-w-2xl'>
            <Section title={t('chat.systemPrompt')} icon={FileText} color='text-cyan-400'>
              <div className='mt-3 space-y-3'>
                <div className='flex gap-2'>
                  {(['default', 'custom'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setPromptMode(mode)}
                      className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                        promptMode === mode
                          ? 'border-blue-500 bg-blue-600/10 text-blue-400'
                          : 'border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {mode === 'default' ? t('chat.promptDefault') : t('chat.promptCustom')}
                    </button>
                  ))}
                  {promptMode === 'default' && (
                    <button
                      onClick={() => setShowPromptPreview((s) => !s)}
                      className='ml-auto text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1'
                    >
                      {showPromptPreview ? <EyeOff size={12} /> : <Eye size={12} />}
                      {showPromptPreview ? t('chat.hide') : t('chat.preview')}
                    </button>
                  )}
                </div>
                {promptMode === 'default' && showPromptPreview && (
                  <pre className='text-xs text-gray-400 bg-gray-950 rounded-xl p-4 border border-gray-800 max-h-60 overflow-y-auto whitespace-pre-wrap font-mono'>
                    {defaultPromptQuery.data?.prompt ?? t('chat.loadingShort')}
                  </pre>
                )}
                {promptMode === 'custom' && (
                  <textarea
                    rows={14}
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    placeholder={t('chat.customPromptPlaceholder')}
                    className='w-full bg-gray-950 border border-gray-800 rounded-xl px-4 py-3 text-sm text-white font-mono focus:outline-none focus:border-blue-500 resize-y'
                  />
                )}
              </div>
            </Section>
            <div className='flex justify-end'>
              <Button
                variant='primary'
                loading={saveSettingsMut.isPending}
                onClick={() => saveSettingsMut.mutate()}
              >
                {t('chat.savePrompt')}
              </Button>
            </div>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}
